/**
 * SME Portal — Final Reports & Delivery Pipeline page.
 * EM finalizes reports and tracks the full pipeline from EM-approved to client delivery.
 * RESTRICTED: Only Engagement Manager (EM) and Engagement Lead (EL) roles.
 */
import React, { useEffect, useState } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button,
  LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Upload, Timer, CheckCircle, Lock, Send } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

const ALLOWED_ROLES = ['engagement_manager', 'engagement_lead', 'super_admin',
  'em_security_compliance', 'em_ai_analytics', 'em_development_operations',
  'em_cloud_strategy_adoption', 'em_productivity_collaboration', 'em_baseline_architecture'];

interface PipelineReport {
  id: string;
  assessment_id: string | null;
  domain: string;
  category: string;
  organization_name: string;
  file_name: string | null;
  status: string;
  status_label: string;
  report_type: string;
  uploaded_at: string | null;
  expires_at: string | null;
  days_remaining: number | null;
  is_expired: boolean;
  download_count: number;
  updated_at: string | null;
}

const STATUS_COLORS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  em_approved: 'info',
  playback_scheduled: 'warning',
  feedback_incorporated: 'info',
  final_uploaded: 'success',
};

const FinalReports: React.FC = () => {
  const [reports, setReports] = useState<PipelineReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<PipelineReport | null>(null);
  const [finalizing, setFinalizing] = useState(false);

  const loadReports = () => {
    authFetch('/api/sme/reports/pipeline')
      .then((r) => r.json())
      .then((data) => { if (data.success) setReports(data.reports); })
      .catch(() => setError('Failed to load report pipeline'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });
  };

  useEffect(() => {
    authFetch('/api/auth/me')
      .then((r) => r.json())
      .then((data) => {
        const role = data.user?.role || '';
        setUserRole(role);
        if (!ALLOWED_ROLES.includes(role)) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
        loadReports();
      })
      .catch(() => { setError('Failed to check access'); setLoading(false); });
  }, []);

  const handleFinalize = async () => {
    if (!selectedReport) return;
    setFinalizing(true);
    try {
      const res = await authFetch(`/api/sme/reports/${selectedReport.id}/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.success) {
        setFinalizeDialogOpen(false);
        setSelectedReport(null);
        loadReports();
      } else {
        setError(data.error || 'Failed to finalize report');
      }
    } catch {
      setError('Failed to finalize report');
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  if (accessDenied) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Lock sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h5" gutterBottom>Access Restricted</Typography>
          <RefreshButton onRefresh={loadReports} loading={loading} lastRefreshed={lastRefreshed} />
        </Box>
        <Typography color="text.secondary">
          Final Reports & Delivery Tracking is restricted to Engagement Managers and Engagement Leads.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Your current role: <strong>{userRole}</strong>
        </Typography>
      </Box>
    );
  }

  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
        Final Reports Pipeline
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track reports from EM approval through playback to client delivery. Finalize reports to release them to the client portal with a 30-day download window.
      </Typography>

      {reports.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Upload sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No reports in the pipeline</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Reports will appear here once a draft has been approved by the Engagement Manager.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Report File</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Retention</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Downloads</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.domain}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.category}</Typography>
                  </TableCell>
                  <TableCell>{r.organization_name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {r.file_name || 'Not generated'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={r.status_label}
                      size="small"
                      color={STATUS_COLORS[r.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {r.status === 'final_uploaded' && r.days_remaining != null ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Timer fontSize="small" />
                        <Chip
                          label={r.is_expired ? 'Expired' : `${r.days_remaining} days`}
                          size="small"
                          color={r.is_expired ? 'error' : r.days_remaining <= 7 ? 'warning' : 'success'}
                        />
                        {!r.is_expired && (
                          <LinearProgress
                            variant="determinate"
                            value={(r.days_remaining / 30) * 100}
                            sx={{ flex: 1, height: 6, borderRadius: 3, minWidth: 60 }}
                            color={r.is_expired ? 'error' : r.days_remaining <= 7 ? 'warning' : 'success'}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.status === 'final_uploaded' ? (
                      <Chip
                        label={`${r.download_count} downloads`}
                        size="small"
                        variant="outlined"
                        color={r.download_count > 0 ? 'success' : 'default'}
                        icon={r.download_count > 0 ? <CheckCircle /> : undefined}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(r.status === 'em_approved' || r.status === 'playback_scheduled' || r.status === 'feedback_incorporated') ? (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Send />}
                        onClick={() => { setSelectedReport(r); setFinalizeDialogOpen(true); }}
                        sx={{ textTransform: 'none' }}
                      >
                        Finalize & Deliver
                      </Button>
                    ) : r.is_expired ? (
                      <Chip label="Expired" size="small" color="error" />
                    ) : r.status === 'final_uploaded' ? (
                      <Chip label="Delivered" size="small" color="success" icon={<CheckCircle />} />
                    ) : (
                      <Chip label={r.status_label} size="small" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Finalize Confirmation Dialog */}
      <Dialog open={finalizeDialogOpen} onClose={() => setFinalizeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Finalize Report for Client Delivery</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This will deliver the <strong>{selectedReport?.domain}</strong> report to{' '}
            <strong>{selectedReport?.organization_name}</strong> via the client portal.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            The client will have <strong>30 days</strong> to download the report.
            A notification will be sent to the client.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Report: {selectedReport?.file_name || 'Assessment Report'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinalizeDialogOpen(false)} disabled={finalizing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFinalize}
            disabled={finalizing}
            startIcon={finalizing ? <CircularProgress size={16} /> : <Send />}
          >
            {finalizing ? 'Finalizing...' : 'Confirm & Deliver'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinalReports;
